import React, {Fragment, FunctionComponent, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {SearchInfo} from "../../common/search-info-grid/SearchInfo";
import {
    ActivationItem, useGetApiV1UserMeQuery,
    useLazyGetApiV1OrderByOrderIdQuery,
    usePostApiV1ActivationListMutation, usePostApiV1ExportMutation,
    usePostApiV1FreeActivationMutation,
    usePostApiV1SerialNumberInternalMutation
} from "../../../api/CloudLicensesManagerApi";
import {Alert, FormControl, InputAdornment, InputLabel, OutlinedInput, Snackbar, Stack} from "@mui/material";
import {PageTemplate} from "../../common/page-template/PageTemplate";
import Typography from "@mui/material/Typography";
import {useNavigate, useParams} from "react-router-dom";
import {TableSkeleton} from "../../common/skeleton/TableSkeleton";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import {
    ProcessStatusNotification,
    toProcessStatus
} from "../../common/process-status-notification/ProcessStatusNotification";
import {ActivationsTable} from "../../common/activation-table/ActivationsTable";
import {OrderInfo} from "./OrderInfo";
import {FreeActivationDialog} from "../../common/free-activation-dialog/FreeActivationDialog";
import {ErrorMessage} from "../../common/error-message/ErrorMessage";
import {ActionButton} from "../../common/page-template/ActionButton";
import {AppRoute} from "../../../book/AppRoute";
import {DottedListSkeleton} from "../../common/skeleton/DottedListSkeleton";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import {Constants} from "../../../book/Constants";
import {CreateInternalSerialNumber} from "./CreateInternalSerialNumber";
import {Loader} from "../../common/loader/Loader";
import {Authorizations} from "../../../book/Authorizations";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import GetAppIcon from '@mui/icons-material/GetApp';
import {ExportUtils} from "../../../book/ExportUtils";

export const OrderDetails: FunctionComponent = () => {
    const {orderId} = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [state, setState] = useState<OrderDetailsState>({
        currentPage: 0,
        pageSize: 50,
        search: '',
        openCopiedToClipboardSnackbar: false,
        createSerialNumberDialogOpen: false,
        openCopyOrder: false
    });
    const {data: userMe, isLoading: isMeLoading} = useGetApiV1UserMeQuery(undefined)
    const [getOrder, {
        isLoading: isOrderLoading,
        isError: isOrderError,
        data: orderData,
    }] = useLazyGetApiV1OrderByOrderIdQuery();
    const [searchActivations, {
        data: searchActivationsData,
        isLoading: isActivationsLoading,
        isError: isActivationsError,
        reset: resetSearchActivations
    }] = usePostApiV1ActivationListMutation();
    const [freeActivation, {
        data: freeActivationData,
        isLoading: isFreeActivationLoading,
        error: freeActivationError,
        reset: resetFreeActivation
    }] = usePostApiV1FreeActivationMutation();
    const [createSerialNumber, {
        data: createSerialNumberData,
        isLoading: isCreateSerialNumberLoading,
        isError: isCreateSerialNumberError,
        reset: resetCreateSerialNumber
    }] = usePostApiV1SerialNumberInternalMutation();

    useEffect(() => {
        if (orderId) {
            getOrder({orderId: orderId});
        }
    }, [orderId]);

    const loadActivations = () => {
        searchActivations({
            activationListRequest: {
                search: state.search.length ? state.search : null,
                page: state.currentPage,
                pageSize: state.pageSize,
                orderId: orderId
            }
        });
    }

    useEffect(() => {
        loadActivations();
    }, [state.search, state.currentPage, state.pageSize, orderId]);

    const [exportMutation, {
        isLoading: exportIsLoading}] = usePostApiV1ExportMutation();

    const searchResultActivations = searchActivationsData?.payload?.activations ?? [];
    const searchResultCount = searchActivationsData?.payload?.count ?? 0;
    const order = orderData?.payload?.order;
    const unusedSerialNumbers = orderData?.payload?.unusedSerialNumbers;

    const handleCloseCopiedToClipboardSnackbar = (event?: React.SyntheticEvent<any> | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setState(state => ({...state, openCopiedToClipboardSnackbar: false}));
    };

    const ActivationSearchInfoGrid = () => <SearchInfo
        count={searchResultCount}
        currentPage={state.currentPage}
        pageSize={state.pageSize}
        onPageChange={page => {
            setState(state => ({...state, currentPage: page}));
        }}
        onPageSizeChange={pageSize => {
            setState(state => ({...state, pageSize}));
        }}
        paginationLabel={t("Orders per page")}
        itemsLabel={t("orders")}/>;

    if (!userMe || isMeLoading) {
        return <Loader message={t("Loading page, please wait...")}/>;
    }

    if (userMe.error) {
        return <ErrorMessage message={t("Error while loading page, please try again later")}/>;
    }

    const authorizations = userMe.payload!.authorizations;
    const pageActions: React.ReactElement[] = [];

    if (authorizations?.includes(Authorizations.ActivationCreate)) {
        pageActions.push(<ActionButton label={t("Create offline license")}
                                       onClick={() => {
                                           navigate(AppRoute.OffLineActivation.replace(':orderId', orderId!))
                                       }}
                                       key="create-offline-license-action"
                                       icon={<DescriptionIcon/>}/>);
    }

    if (orderId === Constants.InternalActivationOrderId && authorizations?.includes(Authorizations.SerialNumberCreate)) {
        pageActions.push(<ActionButton label={t("Create serial number")}
                                       onClick={() => {
                                           setState(state => ({...state, createSerialNumberDialogOpen: true}));
                                       }}
                                       key="create-serial-number-action"
                                       icon={<AddShoppingCartIcon/>}/>);
    }

    if (authorizations?.includes(Authorizations.Export)) {
        pageActions.push(<ActionButton onClick={() => {
                const result = exportMutation({exportRequest: { orderId }});
                result.then(data => {
                    if("data" in data) {
                        const blob = data.data as Blob;
                        ExportUtils.downloadFromBlob(blob, `export_clim_${new Date().toISOString()}_${orderId}.csv`);
                    }
                });
            }} disabled={exportIsLoading}
                          label={exportIsLoading ? t("Exporting…") : t("Export data of current order")}
                          icon={<GetAppIcon />} />);
    }

    const PageTitle = () => <Fragment>
        {t("Order - ")}
        {isOrderLoading && '---'}
        {!isOrderLoading && <Fragment>
            {order?.orderNumber}

            {!!order?.orderNumber &&
                <IconButton title={t("Copy order ID")}
                            onClick={async () => {
                                setState(state => ({...state, openCopyOrder: true}));
                                await navigator.clipboard.writeText(order.orderNumber);
                            }}>
                    <ContentCopyIcon fontSize="small"/>
                </IconButton>}
        </Fragment>}
    </Fragment>;

    return <PageTemplate pageTitle={<PageTitle/>}
                         showBackButton
                         actions={pageActions}
                         backLabel={t("Back to order list")}>
        {isOrderError &&
            <ErrorMessage message={t("Error while load order, please check order id and retry.")}/>}

        {isOrderLoading &&
            <DottedListSkeleton rowCount={5}/>}

        {Boolean(order) &&
            <OrderInfo order={order!}
                       unusedSerialNumbers={unusedSerialNumbers ?? []}
                       onUnusedSerialNumberClick={() => {
                           setState(state => ({
                               ...state,
                               openCopiedToClipboardSnackbar: true
                           }));
                       }}/>}

        <Typography variant="h5" color="inherit" sx={{marginTop: '1.7em', marginBottom: '0.7em'}}>
            {t("Activations")}
        </Typography>

        <FormControl fullWidth variant="outlined">
            <InputLabel
                htmlFor="search-products">{t("Search by serial number or hardware ID…")}</InputLabel>
            <OutlinedInput value={state.search}
                           fullWidth
                           label={t("Search by order number, serial number, customer, or department…")}
                           sx={{marginBottom: '20px'}}
                           onChange={e => {
                               setState(state => ({...state, search: e.target.value}))
                           }}
                           endAdornment={<InputAdornment position="end"><SearchIcon/></InputAdornment>}/>
        </FormControl>

        {isActivationsError &&
            <Alert severity="error" sx={{marginTop: '40px'}}>
                {t("Error while loading activations. Please try again or contact the administrator")}
            </Alert>}

        {isActivationsLoading &&
            <TableSkeleton sx={{marginTop: '40px'}}
                           colCount={7}
                           rowCount={5}
                           showPagination/>}

        {Boolean(searchResultActivations) && <Stack spacing={3} sx={{marginBottom: '15px', marginTop: '35px'}}>
            <ActivationSearchInfoGrid/>

            <ActivationsTable activations={searchResultActivations}
                              onFreeActivationClick={(activation) => {
                                  setState(state => ({...state, activationToFree: activation}));
                              }}/>

            <ActivationSearchInfoGrid/>
        </Stack>}

        <Snackbar open={state.openCopiedToClipboardSnackbar} autoHideDuration={4000}
                  onClose={handleCloseCopiedToClipboardSnackbar}>
            <Alert onClose={handleCloseCopiedToClipboardSnackbar} severity="success">
                Serial number copied to clipboard
            </Alert>
        </Snackbar>

        <ProcessStatusNotification
            status={toProcessStatus(isFreeActivationLoading, Boolean(freeActivationData?.payload), Boolean(freeActivationError))}
            onClose={() => resetFreeActivation()}
            onLoadingMessage={t("Freeing activation please wait…")}
            onSuccessMessage={t("Activation freed successfully")}
            onErrorMessage={t("Activation freeing failed")}/>

        <FreeActivationDialog open={Boolean(state.activationToFree)}
                              activation={state.activationToFree}
                              onCancelClick={() => {
                                  setState(state => ({
                                      ...state,
                                      activationToFree: undefined
                                  }));
                              }}
                              onOkClick={() => {
                                  const activationId = state.activationToFree?.activationId;

                                  setState(state => ({
                                      ...state,
                                      activationToFree: undefined
                                  }));

                                  freeActivation({
                                      freeActivationRequest: {
                                          activationId: activationId!
                                      }
                                  }).then(_ => {
                                      resetSearchActivations();
                                      loadActivations();
                                      if (orderId) {
                                          getOrder({orderId: orderId});
                                      }
                                  });
                              }}/>

        <CreateInternalSerialNumber
            onCancelClick={() => {
                setState(state => ({...state, createSerialNumberDialogOpen: false}));
            }}
            onOkClick={({product, notes}) => {
                setState(state => ({...state, createSerialNumberDialogOpen: false}));
                createSerialNumber({createSToneInternalSerialNumberRequest: {productId: product, notes}});
                getOrder({
                    orderId: orderId as string
                });
            }}
            open={state.createSerialNumberDialogOpen}/>

        <ProcessStatusNotification
            status={toProcessStatus(isCreateSerialNumberLoading, Boolean(createSerialNumberData?.payload), Boolean(isCreateSerialNumberError))}
            onClose={() => resetCreateSerialNumber()}
            onLoadingMessage={t("Creating serial number please wait…")}
            onSuccessMessage={t("Creation serial number successfully")}
            onErrorMessage={t("Creation serial number failed")}/>

        <Snackbar open={state.openCopyOrder}
                  autoHideDuration={3000}
                  onClose={() => setState(state => ({...state, openCopyOrder: false}))}>
            <Alert
                onClose={() => setState(state => ({...state, openCopyOrder: false}))}
                severity="success"
                role="message"
                sx={{width: '100%'}}>
                {t("Order ID copied")}
            </Alert>
        </Snackbar>
    </PageTemplate>
}

interface OrderDetailsState {
    currentPage: number;
    pageSize: number;
    search: string;
    openCopiedToClipboardSnackbar: boolean;
    activationToFree?: ActivationItem;
    createSerialNumberDialogOpen: boolean;
    openCopyOrder: boolean;
}